import type { TenantOutcome } from '../schemas/bulk-migration';

/** The two facts a selection reads off a row. */
export interface SelectableRow {
    readonly outcome: TenantOutcome;
    readonly tenant: { readonly tenantId: string };
}

/**
 * Which tenants of a finished dry-run the operator means to migrate.
 *
 * ## Two shapes, because the table is paged
 *
 * The default is "every migratable tenant" — including the ones on pages not
 * loaded yet — so it cannot be a list of ids. It is `all-planned` minus what
 * the operator unticked. "None" and anything built up from it is an explicit
 * list. Only `planned` tenants are ever selectable: the runner refuses a launch
 * naming any other (`TENANTS_NOT_PLANNED`), so offering one would only produce
 * that refusal.
 */
export type Selection =
    | { readonly kind: 'all-planned'; readonly excluded: readonly string[] }
    | { readonly kind: 'explicit'; readonly included: readonly string[] };

/** The runner's cap on one launch. */
export const MAX_LAUNCH_TENANTS = 1000;

export function selectAllPlanned(): Selection {
    return { kind: 'all-planned', excluded: [] };
}

export function selectNone(): Selection {
    return { kind: 'explicit', included: [] };
}

export function isSelectable(row: Pick<SelectableRow, 'outcome'>): boolean {
    return row.outcome === 'planned';
}

export function isSelected(selection: Selection, row: SelectableRow): boolean {
    if (!isSelectable(row)) return false;
    const id = row.tenant.tenantId;
    return selection.kind === 'all-planned'
        ? !selection.excluded.includes(id)
        : selection.included.includes(id);
}

/** Ticks or unticks one tenant. A tenant that is not selectable leaves the selection as it was. */
export function toggle(selection: Selection, row: SelectableRow): Selection {
    if (!isSelectable(row)) return selection;
    const id = row.tenant.tenantId;
    if (selection.kind === 'all-planned') {
        return selection.excluded.includes(id)
            ? { kind: 'all-planned', excluded: selection.excluded.filter((x) => x !== id) }
            : { kind: 'all-planned', excluded: [...selection.excluded, id] };
    }
    return selection.included.includes(id)
        ? { kind: 'explicit', included: selection.included.filter((x) => x !== id) }
        : { kind: 'explicit', included: [...selection.included, id] };
}

/** How many are selected, given how many the dry-run planned in total. */
export function selectedCount(selection: Selection, plannedTotal: number): number {
    return selection.kind === 'all-planned'
        ? Math.max(0, plannedTotal - selection.excluded.length)
        : selection.included.length;
}

/**
 * The concrete ids to launch, given every planned id of the dry-run (read in
 * full just before launching). An explicit pick no longer planned is dropped
 * rather than sent to be refused.
 */
export function resolveSelection(selection: Selection, plannedIds: readonly string[]): string[] {
    if (selection.kind === 'all-planned') {
        const excluded = new Set(selection.excluded);
        return plannedIds.filter((id) => !excluded.has(id));
    }
    const planned = new Set(plannedIds);
    return selection.included.filter((id) => planned.has(id));
}

/** Why the launch button is not offered, or `null` when it is. */
export function launchBlocker(count: number): string | null {
    if (count === 0) return 'Select at least one migratable client.';
    if (count > MAX_LAUNCH_TENANTS) {
        return `At most ${MAX_LAUNCH_TENANTS.toLocaleString('en-US')} clients per launch — untick ${(count - MAX_LAUNCH_TENANTS).toLocaleString('en-US')}.`;
    }
    return null;
}
